/**
 * グループ管理ページ - melta UI
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { groupApi, deviceApi } from '../services/api';
import { naturalSort, naturalSortBy } from '../services/utils';
import { Icon } from '../components/icons';
import { Button, Card, Alert, Input, Select, Modal } from '../components/ui';

export default function Groups() {
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupThings, setGroupThings] = useState([]);
  const [allThings, setAllThings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 新規グループ
  const [showCreate, setShowCreate] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDesc, setNewGroupDesc] = useState('');
  const [creating, setCreating] = useState(false);
  const groupNameRef = useRef(null);

  // Thing追加
  const [showAddThing, setShowAddThing] = useState(false);
  const [addThingName, setAddThingName] = useState('');

  // データ取得
  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const data = await groupApi.list();
      setGroups(naturalSortBy(data.groups || [], 'groupName'));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAllThings = useCallback(async () => {
    try {
      const data = await deviceApi.list({ maxResults: 100 });
      setAllThings((data.devices || []).filter(d => d.thingName.startsWith('drone-')));
    } catch (err) {
      console.warn('デバイス一覧取得失敗:', err.message);
    }
  }, []);

  useEffect(() => {
    fetchGroups();
    fetchAllThings();
  }, [fetchGroups, fetchAllThings]);

  // グループ選択
  const selectGroup = async (group) => {
    setSelectedGroup(group);
    try {
      const [detail, thingsData] = await Promise.all([
        groupApi.get(group.groupName),
        groupApi.listThings(group.groupName),
      ]);
      setSelectedGroup({ ...group, ...detail });
      setGroupThings([...(thingsData.things || [])].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })));
    } catch (err) {
      console.error('Group detail error:', err);
    }
  };

  // グループ作成
  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!newGroupName.trim()) return;

    if (!/^[a-zA-Z0-9_-]+$/.test(newGroupName.trim())) {
      alert('グループ名は英数字・ハイフン・アンダースコアのみ使用できます。');
      return;
    }

    setCreating(true);
    try {
      await groupApi.create({
        groupName: newGroupName.trim(),
        description: newGroupDesc.trim(),
      });
      setNewGroupName('');
      setNewGroupDesc('');
      setShowCreate(false);
      await fetchGroups();
    } catch (err) {
      alert('グループ作成エラー: ' + err.message);
    } finally {
      setCreating(false);
    }
  };

  // グループ削除
  const handleDeleteGroup = async (groupName) => {
    if (!window.confirm(`"${groupName}" を削除しますか？`)) return;
    try {
      await groupApi.delete(groupName);
      if (selectedGroup?.groupName === groupName) {
        setSelectedGroup(null);
        setGroupThings([]);
      }
      await fetchGroups();
    } catch (err) {
      alert('削除エラー: ' + err.message);
    }
  };

  // Thing追加
  const notInGroup = allThings.filter(t => !groupThings.includes(t.thingName)).sort((a, b) => naturalSort(a.thingName, b.thingName));

  const handleAddThing = async (e) => {
    e.preventDefault();
    if (!addThingName) return;
    try {
      await groupApi.addThing(selectedGroup.groupName, addThingName);
      setGroupThings(prev => [...prev, addThingName]);
      setAddThingName('');
      setShowAddThing(false);

      setTimeout(async () => {
        try {
          const thingsData = await groupApi.listThings(selectedGroup.groupName);
          setGroupThings([...(thingsData.things || [])].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })));
        } catch {}
      }, 1500);
    } catch (err) {
      alert('追加エラー: ' + err.message);
    }
  };

  // Thing除外
  const handleRemoveThing = async (thingName) => {
    if (!window.confirm(`"${thingName}" をグループから除外しますか？`)) return;
    try {
      await groupApi.removeThing(selectedGroup.groupName, thingName);
      setGroupThings(prev => prev.filter(t => t !== thingName));
    } catch (err) {
      alert('除外エラー: ' + err.message);
    }
  };

  useEffect(() => {
    if (showCreate) setTimeout(() => groupNameRef.current?.focus(), 50);
  }, [showCreate]);

  const groupDisplayName = (group) =>
    group.properties?.thingGroupDescription || group.groupName;

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <p className="text-sm text-slate-500">Group作成・管理</p>

      {error && <Alert variant="error" onClose={() => setError(null)}>{error}</Alert>}

      <div className="grid md:grid-cols-2 gap-6">
        {/* グループ一覧 */}
        <div className="space-y-3">
          <Card title="グループ一覧" padding="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : groups.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              グループなし
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {groups.map((group) => (
                <div
                  key={group.groupName}
                  className={`
                    flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors
                    ${selectedGroup?.groupName === group.groupName
                      ? 'bg-primary-50'
                      : 'hover:bg-gray-50'
                    }
                  `}
                  onClick={() => selectGroup(group)}
                >
                  <Icon name="folder" className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 truncate">
                      {groupDisplayName(group)}
                    </div>
                    {group.properties?.thingGroupDescription && (
                      <div className="text-xs text-slate-500 truncate">
                        {group.groupName}
                      </div>
                    )}
                  </div>
                  <button
                    className="p-1.5 rounded text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                    onClick={(e) => { e.stopPropagation(); handleDeleteGroup(group.groupName); }}
                    title="削除"
                  >
                    <Icon name="trash" className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </Card>
          <Button variant="secondary" onClick={() => setShowCreate(true)}>
            新規グループ
          </Button>
        </div>

        {/* グループ詳細 */}
        <Card padding="p-0">
          {selectedGroup ? (
            <div>
              <div className="px-4 py-4 border-b border-slate-200">
                <div className="flex items-center gap-2">
                  <Icon name="folder" className="w-5 h-5 text-primary-500" />
                  <h3 className="font-semibold text-slate-900">
                    {groupDisplayName(selectedGroup)}
                  </h3>
                </div>
                <p className="text-xs text-slate-500 mt-1">ID: {selectedGroup.groupName}</p>
              </div>

              <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                <span className="text-sm font-medium text-slate-700">
                  所属機体 ({groupThings.length})
                </span>
                <Button size="sm" variant="secondary" onClick={() => setShowAddThing(true)}>
                  <Icon name="plus" className="w-4 h-4" />
                  追加
                </Button>
              </div>

              {groupThings.length > 0 ? (
                <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
                  {groupThings.map((thingName) => (
                    <div key={thingName} className="flex items-center gap-3 px-4 py-2.5">
                      <Icon name="drone" className="w-4 h-4 text-slate-400" />
                      <span className="flex-1 text-sm text-slate-700">{thingName}</span>
                      <button
                        className="p-1 rounded text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                        onClick={() => handleRemoveThing(thingName)}
                        title="除外"
                      >
                        <Icon name="x" className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  機体なし
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <Icon name="chevronLeft" className="w-8 h-8 mb-2" />
              <p>グループを選択してください</p>
            </div>
          )}
        </Card>
      </div>

      {/* 新規グループ作成モーダル */}
      <Modal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        title="新規グループ作成"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowCreate(false)}>
              キャンセル
            </Button>
            <Button onClick={handleCreateGroup} loading={creating}>
              作成
            </Button>
          </>
        }
      >
        <form onSubmit={handleCreateGroup} className="space-y-4">
          <div>
            <Input
              ref={groupNameRef}
              label="グループ名（ID）"
              required
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              placeholder="例: fleet-A, tokyo-group"
            />
            <p className="text-xs text-slate-500 mt-1">
              英数字・ハイフン・アンダースコアのみ
            </p>
          </div>
          <div>
            <Input
              label="表示名・説明（日本語OK）"
              value={newGroupDesc}
              onChange={(e) => setNewGroupDesc(e.target.value)}
              placeholder="例: 東京エリア Aチーム"
            />
            <p className="text-xs text-slate-500 mt-1">
              画面上の表示名として使用されます
            </p>
          </div>
        </form>
      </Modal>

      {/* Thing追加モーダル */}
      <Modal
        isOpen={showAddThing}
        onClose={() => setShowAddThing(false)}
        title="機体を追加"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowAddThing(false)}>
              キャンセル
            </Button>
            <Button onClick={handleAddThing} disabled={!addThingName}>
              追加
            </Button>
          </>
        }
      >
        {notInGroup.length > 0 ? (
          <Select
            label="機体"
            required
            value={addThingName}
            onChange={(e) => setAddThingName(e.target.value)}
            options={[
              { value: '', label: '選択してください' },
              ...notInGroup.map(t => ({ value: t.thingName, label: t.thingName }))
            ]}
          />
        ) : (
          <div className="text-center py-4 text-slate-500">
            追加できる機体がありません
          </div>
        )}
      </Modal>
    </div>
  );
}
